// The app's side of page-load recovery. The recovery itself (the ladder, the watchdog, the cards)
// is a lazy chunk, app/recover.js (__RVM.recoverJs), loaded with the engines after load + idle, so
// the start page bundle stays small. Until it is in, first-party pages still get a plain timeout,
// and anything proxied waits for it (a proxied page cannot open before the engines are up anyway).
import { state, byId, active } from "./state.js";
import { errorEl, loaderEl, loaderLabel, setAddr } from "./dom.js";
import { frames, render, reloadFrame } from "./view.js";
import * as P from "./proxy.js";
import { recoverProxy } from "./recovery.js";
import { navigateTab, back } from "./navigation.js";
import { notifyAssetFailure } from "./update.js";
import { get } from "../shared/settings-store.js";
import { ENGINE_LABELS, TRANSPORT_LABELS, normalizeTransport } from "../shared/settings-schema.js";

let R = null;
let loading = null;
export function loadRecovery() {
	if (R) return Promise.resolve(R);
	if (!loading) {
		loading = import(__RVM.recoverJs)
			.then((m) => {
				m.init({
					state, byId, active, errorEl, loaderEl, loaderLabel, setAddr, frames, render, reloadFrame,
					navigateTab, back, notifyAssetFailure, get, recoverProxy, ENGINE_LABELS, TRANSPORT_LABELS, normalizeTransport,
					proxy: {
						reconnect: P.reconnect, setTransport: P.setTransport, transport: P.transport, encodeFor: P.encodeFor, useEngineForTab: P.useEngineForTab,
						retryOn: P.retryOn, availableEngines: P.availableEngines, noteWay: P.noteWay, egressPrefix: P.egressPrefix, steer: P.steer,
						status: P.status, PREFIXES: P.PREFIXES, remote: P.remote, wispUrl: P.wispUrl, setSuspect: P.setTunnelSuspect,
					},
				});
				R = m;
				return m;
			})
			.catch((e) => { loading = null; try { notifyAssetFailure(); } catch (er) { /* best effort */ } throw e; });
	}
	return loading;
}
// Test builds with the test flag only (tools/fault-check.mjs): what the ladder did per tab.
if (__RVM.testHooks && typeof window !== "undefined" && window.__RV_TEST_HOOKS__ === true) {
	window.__rvLadder = () => state.tabs.map((t) => ({ id: t.id, active: t.id === state.activeId, url: t.url, engine: t.engine, loading: !!t.loading, wait: t.wait || null, cause: (t.error && t.error.cause) || null, card: (t.error && t.error.card && t.error.card.title) || (t.error && t.error.title) || null, attempts: t.nav ? t.nav.attempts.map((x) => ({ e: x.engine, t: x.transport, c: x.cause })) : [], done: !!(t.nav && t.nav.done) }));
}
const later = (fn, ...a) => { loadRecovery().then((m) => m[fn](...a)).catch(() => {}); };

// Before the chunk is in: a plain timeout (14 s for a first-party page). A proxied page gets one too
// (40 s), in case the chunk cannot be loaded at all, so a tab is never left on the loader for good.
const early = new Map();
export function startWatch(id) {
	if (R) return R.startWatch(id);
	const t = byId(id);
	clearTimeout(early.get(id));
	early.set(id, setTimeout(() => { const x = byId(id); if (!R && x && x.loading && !x.error) failInternal(id); }, t && t.internal ? 14000 : 40000));
	if (t && t.internal) return;
	later("startWatch", id);
}
export function clearWatch(id) {
	clearTimeout(early.get(id));
	early.delete(id);
	if (R) R.clearWatch(id);
}
export function failInternal(id) {
	if (R) return R.failInternal(id);
	const t = byId(id);
	if (!t) return;
	clearWatch(id);
	t.loading = false;
	t.error = { title: "This page didn’t load", message: "Re:Vault couldn’t open this page. Try again in a moment.", kind: "internal" };
	render();
}
export const checkFrame = (id) => (R ? R.checkFrame(id) : later("checkFrame", id));
export const armProxyErrorCheck = (id) => (R ? R.armProxyErrorCheck(id) : later("armProxyErrorCheck", id));
export const pageLoaded = (id) => (R ? R.pageLoaded(id) : later("pageLoaded", id));
export const remoteFail = (id, sig) => (R ? R.remoteFail(id, sig) : later("remoteFail", id, sig));
export const remoteError = (id, info) => (R ? R.remoteError(id, info) : later("remoteError", id, info));
export const engineLoadFailed = (id) => (R ? R.engineLoadFailed(id) : later("engineLoadFailed", id));
export const reloadWithEngine = (id, name) => (R ? R.reloadWithEngine(id, name) : later("reloadWithEngine", id, name));
export const leaving = (t) => { if (R) R.leaving(t); };
export const countStranded = () => (R ? R.countStranded() : later("countStranded"));
export function retry() {
	if (R) return R.retry();
	const t = active();
	if (t && t.internal) {
		t.error = null; t.loading = true; t.skipLoader = false;
		startWatch(t.id);
		render();
		const f = frames.get(t.id);
		if (f) f.src = f.getAttribute("src") || t.internal;
	} else if (t && t.src) {
		t.error = null; t.loading = true; t.skipLoader = false;
		startWatch(t.id);
		reloadFrame(t.id);
		render();
	}
}
// For the connection dot: "wait" while we reconnect, "offline" while the device is, else "".
export function connState() { return R ? R.connState() : navigator.onLine === false ? "offline" : ""; }

const ICON = '<svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 L22 20 H2 Z"></path><line x1="12" y1="10" x2="12" y2="14"></line><circle cx="12" cy="17" r="0.6" fill="var(--accent)"></circle></svg>';

export function renderError(tab) {
	const e = tab.error || {};
	// A sign-in wall is not a failure, so it does not get failure buttons.
	if (e.kind === "signin") return renderSignInNotice(tab, e);
	if (R) return R.renderError(tab);
	errorEl.innerHTML = '<div class="rv-err-card"><div class="rv-err-icon">' + ICON + '</div><h2 class="rv-err-title"></h2><p class="rv-err-msg"></p><div class="rv-err-actions"><button class="rv-btn rv-btn-primary" data-act="retry">Try again</button></div></div>';
	errorEl.querySelector(".rv-err-title").textContent = e.title || "Something went wrong";
	errorEl.querySelector(".rv-err-msg").textContent = e.message || "";
	errorEl.querySelector('[data-act="retry"]').addEventListener("click", retry);
}
export function renderWait(tab) {
	if (R) return R.renderWait(tab);
	if (loaderEl) loaderEl.removeAttribute("data-wait");
}

// The sign-in wall notice. Two ways out and no dead ends: go back to where you were, or load it
// anyway. "Try anyway" exists because this detection is a URL guess, and a guess must never be
// the last word on whether somebody can reach a page.
function renderSignInNotice(tab, e) {
	errorEl.innerHTML =
		'<div class="rv-err-card">' +
		'<div class="rv-err-icon">' + ICON + "</div>" +
		'<h2 class="rv-err-title"></h2>' +
		'<p class="rv-err-msg"></p>' +
		'<div class="rv-err-actions">' +
		'<button class="rv-btn rv-btn-primary" data-act="back">Go back</button>' +
		'<button class="rv-btn" data-act="anyway">Try anyway</button>' +
		"</div></div>";
	errorEl.querySelector(".rv-err-title").textContent = e.title || "This site blocks sign-in through a proxy";
	errorEl.querySelector(".rv-err-msg").textContent = e.message || "";
	errorEl.querySelector('[data-act="back"]').addEventListener("click", () => {
		if (tab.hi > 0) back();
		else { tab.error = null; tab.loading = false; tab.view = "start"; render(); }
	});
	errorEl.querySelector('[data-act="anyway"]').addEventListener("click", () => {
		navigateTab(tab.id, e.url || tab.url, { force: true });
	});
}

// Surface engine/transport-level failures that escape the iframe.
export function initGlobalErrorHandlers() {
	let transportFails = 0;
	const onErr = (msg) => {
		const m = String(msg || "");
		// A dead bare-mux transport ("all clients returned an invalid MessagePort") is the classic
		// stuck state (a stale worker path from a prior build). Reconnect first; if it keeps failing,
		// a clean recovery reload, so nobody has to Ctrl+Shift+R.
		if (/invalid messageport|bare-mux/i.test(m)) {
			P.reconnect();
			if (++transportFails >= 2) { try { recoverProxy("transport-dead"); } catch (e) {} }
			return;
		}
		if (/websocket|libcurl|transport|wisp|bare/i.test(m)) P.reconnect();
		// A failed dynamic import / chunk load usually means a pruned previous-build asset.
		if (/failed to load|importscripts|chunkloaderror|loading (?:chunk|module).*failed|error loading dynamically imported module/i.test(m)) {
			try { notifyAssetFailure(); } catch (e) {}
		}
	};
	window.addEventListener("unhandledrejection", (e) => onErr(e.reason && (e.reason.message || e.reason)));
	window.addEventListener("error", (e) => onErr(e.message));
}
